import { useMutation, useQueryClient } from '@tanstack/react-query';
import { STPSubmission, PaginationParams } from '@/types';
import { useFetch, usePaginatedFetch } from './useApi';
import { apiClient } from '@/lib/api';

const STP_SUBMISSIONS_KEY = 'stp-submissions';

export interface STPIssue {
  code: string;
  severity: 'ERROR' | 'WARNING';
  employee?: string;
  message: string;
}

export interface STPPrepareResult {
  submission: STPSubmission;
  issues: STPIssue[];
  status: string;
}

export interface STPDashboard {
  byStatus: Record<string, number>;
  payRunsAwaitingSubmission: { id: string; paymentDate: string }[];
  totalSubmissions: number;
}

export function useSTPSubmissions(params?: { status?: string } & PaginationParams) {
  return usePaginatedFetch<{ data: STPSubmission[]; meta: { pagination: { total: number } } }>(
    STP_SUBMISSIONS_KEY,
    '/stp/submissions',
    {
      page: params?.page || 1,
      limit: params?.limit || 20,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    },
    {
      status: params?.status,
    }
  );
}

export function useSTPSubmission(id: string) {
  return useFetch<STPSubmission>(
    [STP_SUBMISSIONS_KEY, id],
    `/stp/submissions/${id}`,
    undefined,
    { enabled: !!id }
  );
}

export function useSTPPayload(id: string) {
  return useFetch<any>(
    [STP_SUBMISSIONS_KEY, id, 'payload'],
    `/stp/submissions/${id}/payload`,
    undefined,
    { enabled: !!id }
  );
}

export function useSTPDashboard() {
  return useFetch<STPDashboard>([STP_SUBMISSIONS_KEY, 'dashboard'], '/stp/dashboard');
}

/** Prepare (or refresh) the STP submission for a pay run, with validation. */
export function usePrepareSTPSubmission() {
  const queryClient = useQueryClient();
  return useMutation<STPPrepareResult, Error, string>({
    mutationFn: (payRunId) => apiClient.post(`/stp/submissions/from-payrun/${payRunId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [STP_SUBMISSIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['pay-runs'] });
    },
  });
}

/** Submit a prepared submission (requires SBR2 credentials to actually lodge). */
export function useSubmitSTPToATO() {
  const queryClient = useQueryClient();
  return useMutation<{ submission: STPSubmission; lodged: boolean; reason?: string; message?: string }, Error, string>({
    mutationFn: (submissionId) => apiClient.post(`/stp/submissions/${submissionId}/submit`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [STP_SUBMISSIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [STP_SUBMISSIONS_KEY, id] });
    },
  });
}
